import isEmpty from "lodash/isEmpty";
import mapValues from "lodash/mapValues";
import merge from "lodash/merge";
import { useFiltersV2Select } from "PFApp/components/filters_v2";
import { getFilterName } from "PFApp/components/filters_v2/helpers/get_filter_by_identifier";
import { useLatestFilters } from "PFCore/hooks/queries/filters/use_latest_filters";
import { DEFAULT_LATEST_FILTERS_KEY } from "PFCore/services/filters";
import { FiltersV2, FilterV2WithValueAndDefault, FilterV2WithValueOnly } from "PFTypes/filters_v2";
import { useMemo } from "react";

const mergeDefaultValues = (
  filters: FiltersV2<FilterV2WithValueOnly>,
  defaults?: FiltersV2<FilterV2WithValueOnly>
): FiltersV2<FilterV2WithValueAndDefault> =>
  merge(
    mapValues(defaults, (filter) => ({
      ...filter,
      value: null,
      defaultValue: defaults?.[getFilterName(filter)]?.value
    })),
    filters
  );

export const useLastUsedFilters = () => {
  const { collection, persistent, id } = useFiltersV2Select("collection", "persistent", "id");
  const {
    data: defaultFilters,
    isLoading,
    isFetching,
    isError
  } = useLatestFilters(
    { target: collection!, key: DEFAULT_LATEST_FILTERS_KEY },
    { enabled: !!collection && persistent }
  );
  const { data: latestFilters, ...defaultFiltersQuery } = useLatestFilters(
    { target: collection!, key: id },
    { enabled: !!collection && persistent }
  );

  return useMemo(
    () => ({
      defaultFilters,
      lastUsedFilters:
        defaultFiltersQuery.isLoading || !latestFilters
          ? undefined
          : {
              mainFilters: mergeDefaultValues(
                latestFilters.collection.mainFilters,
                defaultFilters?.collection.mainFilters
              ),
              childrenFilters: mergeDefaultValues(
                latestFilters.collection.childrenFilters || {},
                defaultFilters?.collection.childrenFilters
              ),
              areChildrenOn: !isEmpty(latestFilters.collection.childrenFilters)
            },
      isLoading: isLoading || defaultFiltersQuery.isLoading,
      isFetching: isFetching || defaultFiltersQuery.isFetching,
      isError: isError || defaultFiltersQuery.isError
    }),
    [
      defaultFilters,
      defaultFiltersQuery.isError,
      defaultFiltersQuery.isFetching,
      defaultFiltersQuery.isLoading,
      isError,
      isFetching,
      isLoading,
      latestFilters
    ]
  );
};
